/**
 * Analytics Connectors admin panel — connect Google Analytics / Google Ads /
 * CRM so the My WebsiteTech dashboard flips from `estimated` to `live`.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, Plug, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import usePageSeo from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMS = {
  google_analytics: [
    { key: "property_id",          label: "GA4 Property ID",     placeholder: "123456789" },
    { key: "service_account_json", label: "Service-account JSON", placeholder: "{...}", type: "textarea" },
  ],
  google_ads: [
    { key: "customer_id",    label: "Customer ID",     placeholder: "123-456-7890" },
    { key: "developer_token", label: "Developer Token", placeholder: "" },
    { key: "refresh_token",  label: "Refresh Token",   placeholder: "" },
  ],
  crm: [
    { key: "provider", label: "CRM provider", placeholder: "hubspot | salesforce | pipedrive" },
    { key: "api_key",  label: "API key",      placeholder: "" },
  ],
};

export default function AnalyticsConnectors() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // provider key currently being connected
  const [form, setForm] = useState({});

  usePageSeo({
    title: "Analytics Connectors | WebsiteTech.pro",
    description: "Connect Google Analytics, Google Ads and your CRM to flip the My WebsiteTech dashboard from estimated to live.",
    noindex: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("wt_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API}/analytics-connectors/status`, { headers });
      setStatus(data);
    } catch (e) {
      toast.error("Please log in to manage connectors");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const connect = async (provider) => {
    const token = localStorage.getItem("wt_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.post(`${API}/analytics-connectors/connect`, { provider, credentials: form }, { headers });
      toast.success(`${provider} connected`);
      setOpen(null);
      setForm({});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Connect failed"); }
  };

  const disconnect = async (provider) => {
    const token = localStorage.getItem("wt_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.delete(`${API}/analytics-connectors/${provider}`, { headers });
      toast.success("Disconnected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="pt-28 pb-20" data-testid="connectors-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/my-websitetech" className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:gap-2 transition-all">
            <ArrowLeft size={14} /> Back to My WebsiteTech
          </Link>
          <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-brand flex items-center gap-2">
            <Plug size={13} /> Analytics Connectors
          </p>
          <h1 className="mt-2 font-heading font-extrabold text-slate-900 text-4xl sm:text-5xl leading-tight">
            Replace estimates<br />
            <span className="text-brand">with live numbers.</span>
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Connect your analytics stack to flip visitors and revenue on the Revenue-Chain dashboard
            from <span className="font-mono text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">estimated</span> to
            <span className="font-mono text-xs bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded ml-1">live</span>.
          </p>

          {loading && !status && (
            <div className="mt-8 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" size={20} /></div>
          )}

          {status && (
            <>
              <div className="mt-8 flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-4" data-testid="data-quality-summary">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Current data quality:</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${status.data_quality?.visitors === "live" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                  Visitors: {status.data_quality?.visitors}
                </span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${status.data_quality?.revenue === "live" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                  Revenue: {status.data_quality?.revenue}
                </span>
              </div>

              <div className="mt-6 space-y-3">
                {status.connectors.map((c) => (
                  <article key={c.provider} data-testid={`connector-${c.provider}`} className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2">
                          {c.connected
                            ? <CheckCircle2 size={16} className="text-emerald-600" />
                            : <AlertCircle size={16} className="text-slate-300" />}
                          <h3 className="font-bold text-slate-900">{c.label}</h3>
                        </div>
                        {c.connected && <p className="mt-1 text-xs text-slate-500 font-mono">{c.preview}</p>}
                      </div>
                      <div>
                        {c.connected ? (
                          <button
                            onClick={() => disconnect(c.provider)}
                            data-testid={`connector-${c.provider}-disconnect`}
                            className="text-xs font-semibold text-rose-600 hover:underline"
                          >
                            Disconnect
                          </button>
                        ) : (
                          <button
                            onClick={() => { setOpen(c.provider); setForm({}); }}
                            data-testid={`connector-${c.provider}-connect`}
                            className="btn-primary rounded-lg px-4 py-2 text-xs font-semibold"
                          >
                            Connect
                          </button>
                        )}
                      </div>
                    </div>

                    {open === c.provider && (
                      <div className="mt-4 border-t border-slate-100 pt-4 space-y-3">
                        {FORMS[c.provider]?.map((f) => (
                          <div key={f.key}>
                            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{f.label}</label>
                            {f.type === "textarea" ? (
                              <textarea
                                data-testid={`connector-${c.provider}-${f.key}`}
                                value={form[f.key] || ""}
                                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                                placeholder={f.placeholder}
                                rows={4}
                                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-brand outline-none"
                              />
                            ) : (
                              <input
                                data-testid={`connector-${c.provider}-${f.key}`}
                                value={form[f.key] || ""}
                                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                                placeholder={f.placeholder}
                                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-brand outline-none"
                              />
                            )}
                          </div>
                        ))}
                        <div className="flex justify-end gap-2 pt-2">
                          <button onClick={() => { setOpen(null); setForm({}); }} className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-3 py-1.5">
                            Cancel
                          </button>
                          <button
                            onClick={() => connect(c.provider)}
                            data-testid={`connector-${c.provider}-save`}
                            className="btn-primary rounded-lg px-4 py-2 text-xs font-semibold"
                          >
                            Save & connect
                          </button>
                        </div>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
